import React, { useEffect, useRef, useState } from "react";
import { canAnimate } from "./motion";
import { humanoidPoints } from "./humanoid";

const TRAIL = 20;
const BUCKETS = [0.08, 0.13, 0.2, 0.3];

// Streamlines drift forward like wind on a map. The cursor stirs them, a click sets a destination,
// holding summons a digital humanoid worker out of the flow, and autopilot sends it all to warp speed.
export default function MomentumField({ hostRef }) {
  const canvasRef = useRef(null);
  const summonRef = useRef(null);
  const [pin, setPin] = useState(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    const layer = summonRef.current;
    const host = hostRef.current;
    if (!canvas || !layer || !host || !canAnimate()) return undefined;
    const ctx = canvas.getContext?.("2d");
    const sctx = layer.getContext?.("2d");
    if (!ctx || !sctx) return undefined;
    let w = 0;
    let h = 0;
    let raf = 0;
    let running = false;
    let onScreen = true;
    let dest = null;
    let warp = 0;
    let shape = [];
    let hold = 0;
    let swallowClick = false;
    const summon = { on: false, x: 0, y: 0, born: 0 };
    const started = performance.now();
    const particles = [];
    const pointer = { x: 0, y: 0, vx: 0, vy: 0, on: false };
    const spawn = (p, fromEdge) => {
      p.x = fromEdge ? -Math.random() * 60 : Math.random() * w;
      p.y = Math.random() * h;
      p.trail = [];
      p.life = 0;
      p.max = 260 + Math.random() * 420;
      p.speed = 0.55 + Math.random() * 0.85;
      p.bucket = Math.floor(Math.random() * BUCKETS.length);
      p.kx = 0;
      p.ky = 0;
      return p;
    };
    const resize = () => {
      const rect = host.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      for (const c of [canvas, layer]) {
        c.width = Math.round(w * dpr);
        c.height = Math.round(h * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      sctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      shape = humanoidPoints(w < 700 ? 34 : 56, w < 700 ? 3.4 : 3.8);
      const count = Math.min(760, Math.max(shape.length + 80, Math.round((w * h) / 1700)));
      while (particles.length < count) particles.push(spawn({}, false));
      particles.length = count;
    };
    const flow = (x, y, t) =>
      Math.sin(x * 0.0023 + t * 0.00011) * 1.1 +
      Math.cos(y * 0.0032 - t * 0.00009) * 1.0 +
      Math.sin((x - y) * 0.0012 + t * 0.00005) * 0.7;
    const frame = (t) => {
      raf = requestAnimationFrame(frame);
      ctx.clearRect(0, 0, w, h);
      sctx.clearRect(0, 0, w, h);
      const gathered = new Path2D();
      const gatheredDots = new Path2D();
      if (summon.on) {
        summon.x += (pointer.x - summon.x) * 0.1;
        summon.y += (pointer.y - summon.y) * 0.1;
      }
      const breath = Math.sin(t * 0.003) * 2;
      const fade = Math.min(1, (t - started) / 1600);
      let pull = 0;
      if (dest) {
        const age = t - dest.born;
        pull = age < 600 ? age / 600 : age < 7600 ? 1 : 1 - (age - 7600) / 1600;
        if (pull <= 0) dest = null;
      }
      const reach = Math.min(190, w * 0.2);
      warp *= 0.975;
      const boost = 1 + warp * 9;
      pointer.vx *= 0.9;
      pointer.vy *= 0.9;
      const paths = BUCKETS.map(() => new Path2D());
      particles.forEach((p, n) => {
        if (summon.on && n < shape.length) {
          const [ox, oy] = shape[n];
          p.vx = ((p.vx || 0) + (summon.x + ox - p.x) * 0.07) * 0.8;
          p.vy = ((p.vy || 0) + (summon.y + oy + breath * (oy < -40 ? 1 : 0.4) - p.y) * 0.07) * 0.8;
          p.x += p.vx;
          p.y += p.vy;
          p.trail.push(p.x, p.y);
          if (p.trail.length > 8) p.trail.splice(0, 2);
          const dot = w < 700 ? 1.8 : 2.3;
          gatheredDots.rect(p.x - dot / 2, p.y - dot / 2, dot, dot);
          if (Math.abs(p.vx) + Math.abs(p.vy) > 0.6) {
            gathered.moveTo(p.trail[0], p.trail[1]);
            for (let i = 2; i < p.trail.length; i += 2) gathered.lineTo(p.trail[i], p.trail[i + 1]);
          }
          return;
        }
        const a = flow(p.x, p.y, t);
        let vx = (Math.cos(a) * p.speed * 0.9 * (1 - warp) + p.speed * 0.6) * boost;
        let vy = Math.sin(a) * p.speed * 0.9 * (1 - warp * 0.9);
        vx += p.kx;
        vy += p.ky;
        p.kx *= 0.93;
        p.ky *= 0.93;
        if (pointer.on) {
          const dx = p.x - pointer.x;
          const dy = p.y - pointer.y;
          const d = Math.hypot(dx, dy) || 1;
          if (d < reach) {
            const f = 1 - d / reach;
            vx += (-dy / d) * f * 2.4 + (dx / d) * f * 1.1 + pointer.vx * f * 0.3;
            vy += (dx / d) * f * 2.4 + (dy / d) * f * 1.1 + pointer.vy * f * 0.3;
          }
        }
        if (dest && pull > 0) {
          const tx = dest.x - p.x;
          const ty = dest.y - p.y;
          const d = Math.hypot(tx, ty) || 1;
          const g = Math.min(3.2, 260 / d) * pull;
          vx += (tx / d) * g - (ty / d) * g * 0.55;
          vy += (ty / d) * g + (tx / d) * g * 0.55;
          if (d < 9) {
            spawn(p, true);
            return;
          }
        }
        p.x += vx;
        p.y += vy;
        p.life += 1;
        p.trail.push(p.x, p.y);
        if (p.trail.length > TRAIL * 2) p.trail.splice(0, 2);
        if (p.x > w + 20 || p.y < -20 || p.y > h + 20 || p.x < -80 || p.life > p.max) {
          spawn(p, Math.random() < 0.7);
          return;
        }
        const path = paths[p.bucket];
        path.moveTo(p.trail[0], p.trail[1]);
        for (let i = 2; i < p.trail.length; i += 2) path.lineTo(p.trail[i], p.trail[i + 1]);
      });
      if (summon.on) {
        const ready = Math.min(1, (t - summon.born) / 900);
        sctx.lineCap = "round";
        sctx.lineWidth = w < 700 ? 1.8 : 2.2;
        sctx.strokeStyle = `rgba(25,25,25,${0.25 + 0.3 * ready})`;
        sctx.stroke(gathered);
        sctx.fillStyle = `rgba(25,25,25,${0.4 + 0.5 * ready})`;
        sctx.fill(gatheredDots);
        if (ready > 0.6) {
          const label = "Worker online";
          sctx.font = "600 12px Manrope, Arial, sans-serif";
          const tw = sctx.measureText(label).width + 28;
          const lx = summon.x - tw / 2;
          const ly = summon.y + (w < 700 ? 44 : 70);
          sctx.globalAlpha = (ready - 0.6) / 0.4;
          sctx.beginPath();
          if (sctx.roundRect) sctx.roundRect(lx, ly, tw, 24, 12);
          else sctx.rect(lx, ly, tw, 24);
          sctx.fillStyle = "#191919";
          sctx.fill();
          sctx.beginPath();
          sctx.arc(lx + 12, ly + 12, 3, 0, Math.PI * 2);
          sctx.fillStyle = "#ffffff";
          sctx.fill();
          sctx.fillStyle = "#ffffff";
          sctx.textBaseline = "middle";
          sctx.fillText(label, lx + 21, ly + 12.5);
          sctx.globalAlpha = 1;
        }
      }
      ctx.lineWidth = 1;
      ctx.lineCap = "round";
      paths.forEach((path, i) => {
        ctx.strokeStyle = `rgba(25,25,25,${BUCKETS[i] * fade})`;
        ctx.stroke(path);
      });
    };
    const sync = () => {
      const want = onScreen && !document.hidden;
      if (want && !running) raf = requestAnimationFrame(frame);
      if (!want && running) cancelAnimationFrame(raf);
      running = want;
    };
    const local = (e) => {
      const rect = host.getBoundingClientRect();
      return [e.clientX - rect.left, e.clientY - rect.top];
    };
    const onMove = (e) => {
      const [x, y] = local(e);
      if (pointer.on) {
        pointer.vx = pointer.vx * 0.6 + (x - pointer.x) * 0.4;
        pointer.vy = pointer.vy * 0.6 + (y - pointer.y) * 0.4;
      }
      pointer.x = x;
      pointer.y = y;
      pointer.on = e.pointerType === "mouse";
    };
    const release = () => {
      window.clearTimeout(hold);
      hold = 0;
      if (!summon.on) return;
      summon.on = false;
      host.classList.remove("is-summoning");
      particles.slice(0, shape.length).forEach((p) => {
        const dx = p.x - summon.x;
        const dy = p.y - summon.y;
        const d = Math.hypot(dx, dy) || 1;
        const burst = 3 + Math.random() * 7;
        p.kx = (dx / d) * burst + (p.vx || 0);
        p.ky = (dy / d) * burst + (p.vy || 0);
        p.life = 0;
      });
    };
    const onDown = (e) => {
      if (e.target.closest("a, button, select, input, .bm-kinetic > span")) return;
      if (e.pointerType === "mouse" && e.button !== 0) return;
      const [x, y] = local(e);
      pointer.x = x;
      pointer.y = y;
      hold = window.setTimeout(() => {
        summon.on = true;
        summon.x = x;
        summon.y = y;
        summon.born = performance.now();
        swallowClick = true;
        host.classList.add("is-summoning");
        window.getSelection?.()?.removeAllRanges?.();
      }, 320);
    };
    const onLeave = () => {
      pointer.on = false;
      release();
    };
    const onEngage = () => {
      warp = 1;
    };
    const onClick = (e) => {
      if (swallowClick) {
        swallowClick = false;
        return;
      }
      if (e.target.closest("a, button, select, input, .bm-kinetic > span")) return;
      if (String(window.getSelection?.() || "").length) return;
      const [x, y] = local(e);
      dest = { x, y, born: performance.now() };
      setPin({ x, y, id: dest.born });
    };
    resize();
    const resizer = window.ResizeObserver ? new ResizeObserver(resize) : null;
    resizer?.observe(host);
    const observer = window.IntersectionObserver
      ? new IntersectionObserver(([entry]) => {
          onScreen = entry.isIntersecting;
          sync();
        })
      : null;
    observer?.observe(host);
    host.addEventListener("pointermove", onMove);
    host.addEventListener("pointerleave", onLeave);
    host.addEventListener("pointerdown", onDown);
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", release);
    host.addEventListener("click", onClick);
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("bm-autopilot", onEngage);
    sync();
    return () => {
      cancelAnimationFrame(raf);
      resizer?.disconnect();
      observer?.disconnect();
      window.clearTimeout(hold);
      host.removeEventListener("pointermove", onMove);
      host.removeEventListener("pointerleave", onLeave);
      host.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", release);
      window.removeEventListener("pointercancel", release);
      host.removeEventListener("click", onClick);
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("bm-autopilot", onEngage);
    };
  }, [hostRef]);
  useEffect(() => {
    if (!pin) return undefined;
    const timer = window.setTimeout(() => setPin(null), 9200);
    return () => window.clearTimeout(timer);
  }, [pin]);
  return (
    <>
      <canvas ref={canvasRef} className="bm-field" aria-hidden="true" />
      <canvas ref={summonRef} className="bm-summon" aria-hidden="true" />
      {pin && (
        <span
          key={pin.id}
          className="bm-pin"
          style={{ left: pin.x, top: pin.y }}
          aria-hidden="true"
        >
          <i />
          <i />
          <svg viewBox="0 0 24 32" width="22" height="30">
            <path
              d="M12 31s10-11.2 10-19A10 10 0 0 0 2 12c0 7.8 10 19 10 19z"
              fill="#191919"
            />
            <circle cx="12" cy="12" r="3.6" fill="#fff" />
          </svg>
          <b>Destination set</b>
        </span>
      )}
    </>
  );
}
